import { InteractionType } from "@/generatedApi/userUserInteractionLoggerApi";
import { useSpotInteraction } from "@/hooks/useSpotInteraction";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";
import { useTapHandler } from "@/hooks/useTapHandler";
import { useDislikeInteraction } from "@/hooks/useDislikeInteraction";
import { SwipeDirection } from "@/constants/swipeConstants";
import { aiLevelPointSuffix, calculateAiLevelPoints } from "@/utils/aiLevelPoints";
import { motion } from "framer-motion";
import { BookmarkIcon, FlagIcon, TrashIcon } from "lucide-react";
import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { DislikeConfirmModal } from "../DislikeConfirmModal";
import {
  UserInteractionSpotCard,
  type UserInteractionSpotCardProps
} from "../UserInteractionSpotCard";

// UserInteractionSpotCardのpropsからハンドラー関連を除外
type UserInteractionSpotCardPropsWithoutHandlers = Omit<
  UserInteractionSpotCardProps,
  "onLeftTap" | "onRightTap" | "isProcessing"
>;

interface SwipeableInteractionCardProps extends UserInteractionSpotCardPropsWithoutHandlers {
  // SwipeableInteractionCard固有のprops
  pageType: "saved" | "visited";
  spotName?: string;
  onInteractionChange?: () => void;
}

export const SwipeableInteractionCard = ({
  // SwipeableInteractionCard固有のprops
  pageType,
  spotName,
  onInteractionChange,
  // UserInteractionSpotCardのprops
  interactionItem,
  ...userInteractionSpotCardProps
}: SwipeableInteractionCardProps) => {
  const [swiped, setSwiped] = React.useState(false);

  const { deleteInteraction, changeInteractionType, createDislikeInteraction } = useSpotInteraction(
    {
      onInteractionChange,
      spotName,
      totalRecommendations: undefined // SavedPageやVisitedPageでは提案数の概念がないためundefined
    }
  );

  const handleDeleteInteraction = useCallback(async () => {
    await deleteInteraction(interactionItem, pageType);
  }, [deleteInteraction, interactionItem, pageType]);

  const handleChangeInteractionType = useCallback(
    async (
      newType: InteractionType,
      method?: "swipe_to_saved" | "swipe_to_visited" | "tap_to_saved" | "tap_to_visited"
    ) => {
      await changeInteractionType(interactionItem, newType, method);
    },
    [changeInteractionType, interactionItem]
  );

  const onSwiped = useCallback(
    (direction: SwipeDirection) => {
      setSwiped(true);

      if (pageType === "saved") {
        if (direction === "right") {
          // Savedページで右スワイプ = 削除
          toast.promise(handleDeleteInteraction(), {
            loading: "削除中...",
            success: "行きたいから削除しました！",
            error: "削除に失敗しました"
          });
        } else {
          // Savedページで左スワイプ = Visitedに移動
          toast.promise(handleChangeInteractionType(InteractionType.VISITED, "swipe_to_visited"), {
            loading: "行ったに移動中...",
            success: `行ったに移動しました！${aiLevelPointSuffix(
              calculateAiLevelPoints(InteractionType.VISITED, InteractionType.SAVED)
            )}`,
            error: "移動に失敗しました"
          });
        }
      } else {
        // pageType === "visited"
        if (direction === "right") {
          // Visitedページで右スワイプ = Savedに移動
          toast.promise(handleChangeInteractionType(InteractionType.SAVED, "swipe_to_saved"), {
            loading: "行きたいに移動中...",
            success: "行きたいに移動しました！",
            error: "移動に失敗しました"
          });
        } else {
          // Visitedページで左スワイプ = 削除
          toast.promise(handleDeleteInteraction(), {
            loading: "削除中...",
            success: "行ったから削除しました！",
            error: "削除に失敗しました"
          });
        }
      }
    },
    [pageType, handleDeleteInteraction, handleChangeInteractionType]
  );

  const { x, rotate, rightOpacity, rightScale, leftOpacity, leftScale, handleDragEnd } =
    useSwipeGesture({
      onSwipe: onSwiped
    });

  const onTapAction = useCallback(
    async (direction: "left" | "right") => {
      if (pageType === "saved") {
        if (direction === "right") {
          // 削除
          await handleDeleteInteraction();
        } else {
          // Visitedに移動
          await handleChangeInteractionType(InteractionType.VISITED, "tap_to_visited");
        }
      } else {
        // pageType === "visited"
        if (direction === "right") {
          // Savedに移動
          await handleChangeInteractionType(InteractionType.SAVED, "tap_to_saved");
        } else {
          // 削除
          await handleDeleteInteraction();
        }
      }
    },
    [pageType, handleDeleteInteraction, handleChangeInteractionType]
  );

  const getTapToastMessage = useCallback(
    (direction: "left" | "right") => {
      if (pageType === "saved") {
        return direction === "right"
          ? "行きたいから削除しました！"
          : `行ったに移動しました！${aiLevelPointSuffix(
              calculateAiLevelPoints(InteractionType.VISITED, InteractionType.SAVED)
            )}`;
      } else {
        return direction === "right" ? "行きたいに移動しました！" : "行ったから削除しました！";
      }
    },
    [pageType]
  );

  const { handleTap, isProcessing } = useTapHandler({
    x,
    swiped,
    setSwiped,
    onAction: onTapAction,
    getToastMessage: getTapToastMessage
  });

  // 興味なしインタラクション
  const [isDisliked, setIsDisliked] = useState(false);

  const handleDislikeConfirmAction = useCallback(async () => {
    try {
      const method = pageType === "saved" ? "saved_to_disliked" : "visited_to_disliked";
      await createDislikeInteraction(interactionItem.spot_id, method);
      toast.success("興味なしに追加しました！");
      setIsDisliked(true);
      setSwiped(true);
      onInteractionChange?.();
    } catch (error) {
      toast.error("エラーが発生しました");
    }
  }, [createDislikeInteraction, interactionItem.spot_id, pageType, onInteractionChange]);

  const { showDislikeModal, setShowDislikeModal, handleDislikeClick, handleDislikeConfirm } =
    useDislikeInteraction({ onConfirm: handleDislikeConfirmAction });

  // スワイプアクションのアイコンとテキストを決定
  const getRightAction = () => {
    if (pageType === "saved") {
      return {
        icon: <TrashIcon size={24} className="text-white" />,
        text: "削除",
        bgColor: "bg-gradient-to-br from-gray-400 to-gray-500",
        actionType: "delete" as const
      };
    } else {
      return {
        icon: <BookmarkIcon size={24} className="text-white" fill="currentColor" />,
        text: "行きたい",
        bgColor: "bg-gradient-to-br from-[#0fff78] to-[#86faff]",
        actionType: "bookmark" as const
      };
    }
  };

  const getLeftAction = () => {
    if (pageType === "saved") {
      return {
        icon: <FlagIcon size={24} className="text-white" fill="currentColor" />,
        text: "行った",
        bgColor: "bg-gradient-to-br from-[#0fff78] to-[#86faff]",
        actionType: "flag" as const
      };
    } else {
      return {
        icon: <TrashIcon size={24} className="text-white" />,
        text: "削除",
        bgColor: "bg-gradient-to-br from-gray-400 to-gray-500",
        actionType: "delete" as const
      };
    }
  };

  const rightAction = getRightAction();
  const leftAction = getLeftAction();

  return swiped ? null : (
    <div style={{ position: "relative", width: "100%", cursor: "grab" }}>
      {/* 右スワイプアクション（ドラッグ時のフィードバック） */}
      <motion.div
        style={{
          position: "absolute",
          top: 30,
          right: -20,
          opacity: rightOpacity,
          scale: rightScale,
          pointerEvents: "none"
        }}
        className={`z-50 flex h-16 w-16 flex-col items-center justify-center rounded-full ${rightAction.bgColor} p-2`}
      >
        {rightAction.icon}
        {rightAction.actionType === "delete" && (
          <span className="text-xs font-semibold text-white">{rightAction.text}</span>
        )}
      </motion.div>

      {/* 左スワイプアクション（ドラッグ時のフィードバック） */}
      <motion.div
        style={{
          position: "absolute",
          top: 30,
          left: -20,
          opacity: leftOpacity,
          scale: leftScale,
          pointerEvents: "none"
        }}
        className={`z-50 flex h-16 w-16 flex-col items-center justify-center rounded-full ${leftAction.bgColor} p-2`}
      >
        {leftAction.icon}
        {leftAction.actionType === "delete" && (
          <span className="text-xs font-semibold text-white">{leftAction.text}</span>
        )}
      </motion.div>

      <motion.div
        style={{
          x,
          rotate,
          touchAction: "pan-y" // スクロールとの競合防止
        }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        onDragEnd={handleDragEnd}
      >
        <UserInteractionSpotCard
          interactionItem={interactionItem}
          {...userInteractionSpotCardProps}
          leftSwipeAction={leftAction.actionType}
          rightSwipeAction={rightAction.actionType}
          forceShowSwipeBars={true}
          onLeftTap={() => handleTap("left")}
          onRightTap={() => handleTap("right")}
          isProcessing={isProcessing}
          onDislikeClick={handleDislikeClick}
          isDisliked={isDisliked}
        />
      </motion.div>

      {/* 興味なし確認モーダル */}
      <DislikeConfirmModal
        isOpen={showDislikeModal}
        onClose={() => setShowDislikeModal(false)}
        onConfirm={handleDislikeConfirm}
      />
    </div>
  );
};
